import "moment/locale/pt-br";
import moment from "moment";
import { useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import ReactDatetime from "react-datetime";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import { FormLabel } from "@mui/material";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles({
  rdtSelected: {
    backgroundColor: themeColorsDesingSystem.secondary.main,
    color: themeColorsDesingSystem.background.main,
  },
});

export default function DateField({
  onChange,
  value,
  isValidDate = undefined,
  disabled = false,
  pattern = "br",
  label = undefined,
  required = false,
  multiple = false,
  initialViewDate = undefined,
}) {
  const classes = useStyles();
  const [lastAnchorDate, setLastAnchorDate] = useState(null);

  const resolvedValue = useMemo(() => {
    if (multiple) return null;
    if (value === undefined || value === null || value === "") return undefined;
    if (moment.isMoment(value)) return value;
    if (value instanceof Date) return moment(value);
    if (typeof value === "string") {
      const fmt = pattern === "br" ? "DD-MM-YYYY" : "MM-DD-YYYY";
      const m = moment(value, fmt, true);
      return m.isValid() ? m : undefined;
    }
    return undefined;
  }, [value, multiple, pattern]);

  let ivd = undefined;
  if (initialViewDate) {
    if (initialViewDate instanceof Date) {
      ivd = moment(initialViewDate);
    } else if (
      typeof initialViewDate === "object" &&
      Number(initialViewDate.year) &&
      Number(initialViewDate.month)
    ) {
      ivd = moment()
        .year(Number(initialViewDate.year))
        .month(Number(initialViewDate.month) - 1)
        .date(1);
    }
  }
  return (
    <>
      {label && (
        <FormLabel
          sx={{
            color: themeColorsDesingSystem.text.primary,
            fontSize: ".875rem",
            fontWeight: "600",
            lineHeight: 1.5,
            mb: 0,
            fontFamily: "Open Sans,sans-serif",
          }}
        >
          {label}
          {required ? " *" : ""}
        </FormLabel>
      )}
      <ReactDatetime
        value={multiple ? null : resolvedValue}
        timeFormat={false}
        isValidDate={isValidDate}
        initialViewDate={ivd}
        onChange={(selectedValue) => {
          if (multiple) {
            const formattedDate = selectedValue.format(
              pattern === "br" ? "DD-MM-YYYY" : "MM-DD-YYYY"
            );
            const currentDates = Array.isArray(value) ? value : [];

            const dateExists = currentDates.includes(formattedDate);

            if (dateExists) {
              const newDates = currentDates.filter(
                (date) => date !== formattedDate
              );
              onChange(newDates);
            } else {
              const newDates = [...currentDates, formattedDate];
              onChange(newDates);
            }
            setLastAnchorDate(selectedValue);
          } else {
            if (moment.isMoment(selectedValue)) {
              onChange(
                selectedValue.format(
                  pattern === "br" ? "DD-MM-YYYY" : "MM-DD-YYYY"
                )
              );
            } else if (typeof selectedValue === "string") {
              onChange(selectedValue);
            }
          }
        }}
        dateFormat={pattern === "br" ? "DD/MM/YYYY" : "MM/DD/YYYY"}
        locale="pt-br"
        inputProps={{
          style: { width: "100%", justifyContent: "flex-start" },
        }}
        pickerProps={{
          style: { backgroundColor: "red" },
        }}
        renderDay={(props, currentDate) => {
          const dateFormat = pattern === "br" ? "DD-MM-YYYY" : "MM-DD-YYYY";
          const formattedDate = currentDate.format(dateFormat);
          const isSelected = multiple
            ? Array.isArray(value) && value.includes(formattedDate)
            : typeof value === "string" && value === formattedDate;

          const handleClick = (e) => {
            if (!multiple) {
              props.onClick && props.onClick(e);
              return;
            }
            e.preventDefault();
            e.stopPropagation();

            const currentDates = Array.isArray(value) ? value : [];

            if (e.shiftKey && lastAnchorDate) {
              const start = moment
                .min(lastAnchorDate, currentDate)
                .clone()
                .startOf("day");
              const end = moment
                .max(lastAnchorDate, currentDate)
                .clone()
                .startOf("day");
              const range = [];
              const cursor = start.clone();
              while (cursor.isSameOrBefore(end, "day")) {
                range.push(cursor.format(dateFormat));
                cursor.add(1, "day");
              }
              const set = new Set(currentDates);
              range.forEach((d) => set.add(d));
              onChange(Array.from(set));
            } else {
              const exists = currentDates.includes(formattedDate);
              if (exists) {
                onChange(currentDates.filter((d) => d !== formattedDate));
              } else {
                onChange([...currentDates, formattedDate]);
              }
              setLastAnchorDate(currentDate);
            }
          };

          return (
            <td
              {...props}
              onClick={handleClick}
              className={`${props.className} ${
                isSelected ? classes.rdtSelected : ""
              }`}
            >
              {currentDate.date()}
            </td>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            disabled={disabled}
            value={
              multiple
                ? Array.isArray(value) && value.length > 1
                  ? `${value.length} datas selecionadas`
                  : value
                : params.value
            }
            sx={{
              justifyContent: "center",
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: themeColorsDesingSystem.primary.main,
                },
                "&.Mui-disabled": {
                  backgroundColor: themeColorsDesingSystem.stone.light,
                  cursor: "default",
                },
              },
              "& .MuiInputBase-input": {
                fontFamily: "Open Sans,sans-serif",
                color: themeColorsDesingSystem.text.primary,
                fontSize: ".875rem",
                padding: "9px 4px 9px 5px!important",
              },
              "& .MuiIconButton-root": {
                padding: 0,
              },
              "& .MuiAutocomplete-root": {
                marginBottom: "0px",
              },
            }}
          />
        )}
      />
    </>
  );
}
